/**
 * Stellar DEX Dashboard Component
 * 
 * Displays Stellar order book, market maker status, and arbitrage opportunities.
 */

import React, { useState, useEffect } from 'react';
import {
  StellarMarketMaker,
  type StellarMarketMakerState,
  AURG_XLM_STELLAR_CONFIG
} from '../services/StellarMarketMaker';
import {
  StellarArbitrageMonitor,
  type StellarOrderBookSnapshot,
  type StellarArbitrageOpportunity,
  type CrossChainArbitrageOpportunity
} from '../services/StellarArbitrageMonitor';
import { grams } from '../services/types/Weight';

const ISSUER_PUBLIC_KEY = 'GDFPCZY5F4QLZQMB5CRR3LF644AP6R76QFY2CSFCZSCELEW5Z532SU7A';
const MARKET_MAKER_PUBLIC_KEY = 'GB73QRFVUQS3TJSRQV2BYNKKVWFSNRR2PZREM6MPIHPV7R4Q7BLTKTQB';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const StellarDEXDashboard: React.FC<{ userWalletAddress?: string }> = ({ userWalletAddress }) => {
  // TODO: Display wallet holdings for: {userWalletAddress || 'No wallet connected'}
  console.log('User wallet:', userWalletAddress); // TODO: Display holdings
  const [orderBook, setOrderBook] = useState<StellarOrderBookSnapshot | null>(null);
  const [marketMakerState, setMarketMakerState] = useState<StellarMarketMakerState | null>(null);
  const [arbitrageOpp, setArbitrageOpp] = useState<StellarArbitrageOpportunity | null>(null);
  const [crossChainOpp, setCrossChainOpp] = useState<CrossChainArbitrageOpportunity | null>(null);
  const [spotPrice, setSpotPrice] = useState<number>(95.12);
  const [xrplPrice, setXrplPrice] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const marketMaker = React.useMemo(() => {
    return new StellarMarketMaker({
      ...AURG_XLM_STELLAR_CONFIG,
      marketMakerPublicKey: MARKET_MAKER_PUBLIC_KEY,
      issuerPublicKey: ISSUER_PUBLIC_KEY
    });
  }, []);
  
  const arbitrageMonitor = React.useMemo(() => {
    return new StellarArbitrageMonitor('AURG', ISSUER_PUBLIC_KEY, 'XLM');
  }, []);
  
  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const [book, state] = await Promise.all([
        arbitrageMonitor.getOrderBook(),
        marketMaker.getState()
      ]);
      
      setOrderBook(book);
      setMarketMakerState(state);
      
      // Check arbitrage
      const opp = await arbitrageMonitor.checkArbitrage(spotPrice, grams(1.0));
      setArbitrageOpp(opp);
      
      // Check cross-chain if XRPL price available
      if (xrplPrice !== null) {
        const crossOpp = await arbitrageMonitor.checkCrossChainArbitrage(xrplPrice);
        setCrossChainOpp(crossOpp);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 15000);
    return () => clearInterval(interval);
  }, [spotPrice, xrplPrice]);
  
  return (
    <div style={{ padding: '40px 20px', fontFamily: "'SF Pro Display', system-ui, sans-serif", maxWidth: '1400px', margin: '0 auto' }}>
      <h1 style={{ fontSize: '42px', fontWeight: '300', marginBottom: '12px', color: '#e5e7eb' }}>🌟 Stellar DEX Dashboard</h1>
      <p style={{ color: '#9ca3af', marginBottom: '32px', fontSize: '16px' }}>
        Real-time Stellar order book, market maker, and arbitrage monitoring for AURG/XLM
      </p>

      {error && (
        <div style={{ background: 'rgba(239, 68, 68, 0.1)', padding: '16px', marginBottom: '24px', borderRadius: '12px', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
          <strong style={{ color: '#fca5a5' }}>Error:</strong> <span style={{ color: '#fecaca' }}>{error}</span>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '32px' }}>
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#e5e7eb', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Gold Spot (USD/g):
          </label>
          <input
            type="number"
            value={spotPrice}
            onChange={(e) => setSpotPrice(parseFloat(e.target.value))}
            step={0.01}
            style={{ width: '100%', padding: '12px 16px', fontSize: '16px', background: 'rgba(0, 0, 0, 0.4)', border: '1px solid rgba(255, 255, 255, 0.12)', borderRadius: '10px', color: '#ffffff', fontFamily: "'SF Pro Display', system-ui, sans-serif" }}
          />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#e5e7eb', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            XRPL Price (USD, optional):
          </label>
          <input
            type="number"
            value={xrplPrice ?? ''}
            onChange={(e) => setXrplPrice(e.target.value ? parseFloat(e.target.value) : null)}
            step={0.01}
            placeholder="Enter XRPL price..."
            style={{ width: '100%', padding: '12px 16px', fontSize: '16px', background: 'rgba(0, 0, 0, 0.4)', border: '1px solid rgba(255, 255, 255, 0.12)', borderRadius: '10px', color: '#ffffff', fontFamily: "'SF Pro Display', system-ui, sans-serif" }}
          />
        </div>
      </div>
      
      {/* Market Maker State */}
      {marketMakerState && (
        <div style={{ background: 'rgba(0, 0, 0, 0.35)', padding: '24px', marginBottom: '32px', borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.12)' }}>
          <h2 style={{ marginTop: 0, marginBottom: '20px', fontSize: '24px', fontWeight: '600', color: '#e5e7eb' }}>📊 Market Maker Status</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px' }}>
            <div>
              <div style={{ color: '#9ca3af', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>AURG Balance</div>
              <div style={{ fontSize: '20px', fontWeight: '600', color: '#60a5fa' }}>
                {parseFloat(marketMakerState.assetBalance).toFixed(4)}
              </div>
            </div>
            <div>
              <div style={{ color: '#9ca3af', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>XLM Balance</div>
              <div style={{ fontSize: '20px', fontWeight: '600', color: '#60a5fa' }}>
                {parseFloat(marketMakerState.baseBalance).toFixed(4)}
              </div>
            </div>
            <div>
              <div style={{ color: '#9ca3af', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Mid Price</div>
              <div style={{ fontSize: '20px', fontWeight: '600', color: '#60a5fa' }}>
                {marketMakerState.midPrice > 0 ? marketMakerState.midPrice.toFixed(4) : 'N/A'}
              </div>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px', marginTop: '20px' }}>
            <div>
              <div style={{ color: '#9ca3af', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Open Bids</div>
              <div style={{ fontSize: '20px', fontWeight: '600', color: '#10b981' }}>
                {marketMakerState.openBids}
              </div>
            </div>
            <div>
              <div style={{ color: '#9ca3af', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Bid Volume</div>
              <div style={{ fontSize: '20px', fontWeight: '600', color: '#10b981' }}>
                {marketMakerState.totalBidVolume}
              </div>
            </div>
            <div>
              <div style={{ color: '#9ca3af', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Open Asks</div>
              <div style={{ fontSize: '20px', fontWeight: '600', color: '#ef4444' }}>
                {marketMakerState.openAsks}
              </div>
            </div>
            <div>
              <div style={{ color: '#9ca3af', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Ask Volume</div>
              <div style={{ fontSize: '20px', fontWeight: '600', color: '#ef4444' }}>
                {marketMakerState.totalAskVolume}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Order Book */}
      {orderBook && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '32px' }}>
          <div style={{ background: 'rgba(0, 0, 0, 0.35)', padding: '20px', borderRadius: '12px', border: '1px solid rgba(16, 185, 129, 0.3)' }}>
            <h3 style={{ marginTop: 0, marginBottom: '16px', color: '#10b981', fontSize: '20px', fontWeight: '600' }}>🟢 Bids (Buy Orders)</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '10px 8px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', color: '#9ca3af', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Price</th>
                  <th style={{ textAlign: 'right', padding: '10px 8px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', color: '#9ca3af', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {orderBook.bids.slice(0, 10).map((bid, i) => (
                  <tr key={i}>
                    <td style={{ padding: '8px', color: '#10b981', fontSize: '14px', fontWeight: '500' }}>
                      {bid.price.toFixed(4)}
                    </td>
                    <td style={{ padding: '8px', textAlign: 'right', color: '#e5e7eb', fontSize: '14px' }}>
                      {bid.amount.toFixed(4)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ background: 'rgba(0, 0, 0, 0.35)', padding: '20px', borderRadius: '12px', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
            <h3 style={{ marginTop: 0, marginBottom: '16px', color: '#ef4444', fontSize: '20px', fontWeight: '600' }}>🔴 Asks (Sell Orders)</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '10px 8px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', color: '#9ca3af', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Price</th>
                  <th style={{ textAlign: 'right', padding: '10px 8px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', color: '#9ca3af', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {orderBook.asks.slice(0, 10).map((ask, i) => (
                  <tr key={i}>
                    <td style={{ padding: '8px', color: '#ef4444', fontSize: '14px', fontWeight: '500' }}>
                      {ask.price.toFixed(4)}
                    </td>
                    <td style={{ padding: '8px', textAlign: 'right', color: '#e5e7eb', fontSize: '14px' }}>
                      {ask.amount.toFixed(4)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {/* Arbitrage Opportunities */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        <div style={{ background: '#fff3cd', padding: '15px', borderRadius: '8px', border: '2px solid #ffc107' }}>
          <h3 style={{ marginTop: 0 }}>⚡ Single-Chain Arbitrage</h3>
          {arbitrageOpp ? (
            <div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#856404', marginBottom: '10px' }}>
                {arbitrageOpp.type === 'mint-sell' ? '📤 MINT & SELL' : '📥 BUY & BURN'}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>DEX Price:</strong> ${arbitrageOpp.dexPrice.toFixed(4)}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>Spot Price:</strong> ${arbitrageOpp.spotPrice.toFixed(4)}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>Profit:</strong> {arbitrageOpp.profitPct.toFixed(2)}% ({arbitrageOpp.profitBps} bps)
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>Size:</strong> {arbitrageOpp.size.toFixed(2)} AURG
              </div>
              <div>
                <strong>Gross Profit:</strong> ${arbitrageOpp.grossProfit.toFixed(2)}
              </div>
            </div>
          ) : (
            <div style={{ color: '#666' }}>No opportunities detected</div>
          )}
        </div>
        
        <div style={{ background: 'rgba(96, 165, 250, 0.1)', padding: '20px', borderRadius: '12px', border: '1px solid rgba(96, 165, 250, 0.3)' }}>
          <h3 style={{ marginTop: 0, fontSize: '20px', fontWeight: '600', color: '#60a5fa' }}>🌐 Cross-Chain Arbitrage</h3>
          {crossChainOpp ? (
            <div style={{ color: '#e5e7eb' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#60a5fa', marginBottom: '10px' }}>
                {crossChainOpp.type === 'stellar-to-xrpl' ? '🌟→💎 STELLAR→XRPL' : '💎→🌟 XRPL→STELLAR'}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong style={{ color: '#60a5fa' }}>Stellar Price:</strong> ${crossChainOpp.stellarPrice.toFixed(4)}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong style={{ color: '#60a5fa' }}>XRPL Price:</strong> ${crossChainOpp.xrplPrice.toFixed(4)}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong style={{ color: '#60a5fa' }}>Profit:</strong> {crossChainOpp.profitPct.toFixed(2)}% ({crossChainOpp.profitBps} bps)
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong style={{ color: '#60a5fa' }}>Size:</strong> {crossChainOpp.size.toFixed(2)} AURG
              </div>
              <div>
                <strong style={{ color: '#60a5fa' }}>Gross Profit:</strong> ${crossChainOpp.grossProfit.toFixed(2)}
              </div>
            </div>
          ) : (
            <div style={{ color: '#9ca3af' }}>
              {xrplPrice === null ? 'Enter XRPL price to enable' : 'No opportunities detected'}
            </div>
          )}
        </div>
      </div>
      
      {/* Refresh Button */}
      <div style={{ marginTop: '32px', textAlign: 'center' }}>
        <button
          onClick={loadData}
          disabled={isLoading}
          style={{
            padding: '14px 32px',
            fontSize: '16px',
            fontWeight: '600',
            background: isLoading ? 'rgba(255, 255, 255, 0.1)' : 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
            color: '#ffffff',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            borderRadius: '10px',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            fontFamily: "'SF Pro Display', system-ui, sans-serif",
            transition: 'all 0.2s'
          }}
        >
          {isLoading ? 'Loading...' : '🔄 Refresh Data'}
        </button>
      </div>
      
      {/* Network Info */}
      <div style={{ marginTop: '24px', padding: '16px', background: 'rgba(96, 165, 250, 0.1)', borderRadius: '12px', border: '1px solid rgba(96, 165, 250, 0.2)', fontSize: '13px', color: '#9ca3af', fontFamily: "'SF Pro Display', system-ui, sans-serif" }}>
        <strong style={{ color: '#60a5fa' }}>Network:</strong> Stellar Mainnet<br />
        <strong style={{ color: '#60a5fa' }}>Issuer:</strong> <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{ISSUER_PUBLIC_KEY}</span><br />
        <strong>Market Maker:</strong> {MARKET_MAKER_PUBLIC_KEY}<br />
        <strong>Asset:</strong> AURG/XLM
      </div>
    </div>
  );
};
